/**
 * Colonna elenco: titolo, tab, ricerca, righe.
 *
 * È la colonna centrale del riferimento. La ricerca sta **qui dentro**, in
 * cima all'elenco che filtra — non nella barra di navigazione, dove non
 * saprebbe cosa cercare.
 */

import type { ReactNode } from "react";
import { cx, SearchField, Segmented } from "../ui/primitives.tsx";

export function ListPanel<T extends string>({
  title,
  action,
  collapsed,
  tabs,
  tab,
  onTab,
  query,
  onQuery,
  searchPlaceholder,
  bare,
  children,
}: {
  /** Assente quando chi ospita l'elenco dice già di cosa si tratta. */
  title?: string;
  action?: ReactNode;
  /** Su schermo stretto l'elenco lascia il posto al dettaglio aperto. */
  collapsed?: boolean;
  tabs?: Array<{ id: T; label: string; count?: number }>;
  tab?: T;
  onTab?: (id: T) => void;
  query?: string;
  onQuery?: (value: string) => void;
  searchPlaceholder?: string;
  /** Senza larghezza né bordo propri: li mette chi lo anima da fuori. */
  bare?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cx(
        "h-full w-full flex-col bg-surface",
        bare ? "flex" : "shrink-0 border-line md:w-[352px] md:border-r",
        !bare && (collapsed ? "hidden md:flex" : "flex"),
      )}
    >
      <div className="flex flex-col gap-3.5 px-5 pb-4 pt-5">
        {title || action ? (
          <div className="flex items-center justify-between gap-3">
            {title ? (
              <h1 className="text-[26px] font-extrabold tracking-tight text-ink">
                {title}
              </h1>
            ) : null}
            {action}
          </div>
        ) : null}

        {tabs && tab && onTab ? (
          <Segmented options={tabs} value={tab} onChange={onTab} />
        ) : null}

        {onQuery ? (
          <SearchField
            value={query ?? ""}
            onChange={onQuery}
            placeholder={searchPlaceholder ?? "Cerca…"}
          />
        ) : null}
      </div>

      <div className="scroll-soft min-h-0 flex-1 overflow-y-auto px-2.5 pb-4">
        {children}
      </div>
    </div>
  );
}

/** Riga di elenco: miniatura, titolo, ora, anteprima, pallino non letti. */
export function ListRow({
  active,
  onClick,
  media,
  title,
  meta,
  preview,
  end,
}: {
  active?: boolean;
  onClick: () => void;
  media?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  preview?: ReactNode;
  end?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "flex w-full items-start gap-3 rounded-card-lg p-2.5 text-left",
        "transition-all duration-calm ease-soft",
        active ? "bg-surface-muted" : "hover:bg-surface-muted/70",
      )}
    >
      {media}
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-[14px] font-bold text-ink">
            {title}
          </span>
          {meta ? (
            <span className="shrink-0 text-[11px] tabular-nums text-ink-faint">
              {meta}
            </span>
          ) : null}
        </span>
        {preview ? (
          <span className="mt-0.5 flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-[13px] leading-snug text-ink-soft">
              {preview}
            </span>
            {end}
          </span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * Colonna di dettaglio.
 *
 * Su desktop sta sempre accanto all'elenco; su schermo stretto compare solo
 * quando c'è qualcosa da mostrare, e l'elenco si ritira.
 */
export function DetailPanel({
  open,
  children,
}: {
  open: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cx(
        "min-w-0 flex-1 flex-col bg-surface",
        open ? "flex" : "hidden md:flex",
      )}
    >
      {children}
    </div>
  );
}
